import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2, Plus } from "lucide-react";
import api from "../../lib/api";
import { AdminHeader, Btn, DataTable, Card, Input, Localized } from "../../components/admin/Fields";
import ImageUpload from "../../components/admin/ImageUpload";

const empty = () => ({
  slug: "",
  name: { tr: "", en: "", ar: "" },
  sector: { tr: "", en: "", ar: "" },
  tagline: { tr: "", en: "", ar: "" },
  description: { tr: "", en: "", ar: "" },
  long_description: { tr: "", en: "", ar: "" },
  services: [],
  logo: "",
  cover_image: "",
  gallery: [],
  website_url: "",
  phone: "",
  email: "",
  address: { tr: "", en: "", ar: "" },
  map_lat: "",
  map_lng: "",
  working_hours: { tr: "", en: "", ar: "" },
  established: "",
  country: "",
  bitrix_form_key: "",
  featured: true,
  order: 0,
});

const AdminGroupCompaniesPage = () => {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty());
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/admin/group-companies").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const payload = {
        ...form,
        established: form.established ? Number(form.established) : null,
        order: Number(form.order) || 0,
        map_lat: form.map_lat === "" || form.map_lat == null ? null : Number(form.map_lat),
        map_lng: form.map_lng === "" || form.map_lng == null ? null : Number(form.map_lng),
      };
      if (editing?.new) await api.post("/admin/group-companies", payload);
      else await api.put(`/admin/group-companies/${editing.id}`, payload);
      toast.success("Kaydedildi"); setEditing(null); load();
    } catch (err) { toast.error(err?.response?.data?.detail || err.message); } finally { setBusy(false); }
  };
  const remove = async (r) => {
    if (!window.confirm(`"${r.slug}" silinsin mi?`)) return;
    await api.delete(`/admin/group-companies/${r.id}`); load();
  };

  if (editing) return (
    <div data-testid="admin-group-form">
      <AdminHeader eyebrow="Holding" title={editing.new ? "Yeni Grup Şirketi" : "Şirketi Düzenle"}
        action={<div className="flex gap-3"><Btn variant="ghost" onClick={() => setEditing(null)}>İptal</Btn><Btn onClick={save} disabled={busy} testId="group-save">Kaydet</Btn></div>} />
      <form onSubmit={save} className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card title="Genel Bilgiler">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Input label="Slug" value={form.slug} onChange={(v) => setForm({ ...form, slug: v })} testId="group-slug" />
              <Input label="Kuruluş Yılı" type="number" value={form.established} onChange={(v) => setForm({ ...form, established: v })} />
              <Input label="Ülke Kodu" value={form.country} onChange={(v) => setForm({ ...form, country: v })} />
              <Input label="Web Sitesi URL (dış bağlantı)" value={form.website_url} onChange={(v) => setForm({ ...form, website_url: v })} testId="group-website" className="md:col-span-2" />
              <Input label="Bitrix Form Anahtarı (opsiyonel)" value={form.bitrix_form_key} onChange={(v) => setForm({ ...form, bitrix_form_key: v })} testId="group-bitrix-key" placeholder="ör: iletisim | is-ortagi | firat-real-estate" />
              <Input label="Sıra" type="number" value={form.order} onChange={(v) => setForm({ ...form, order: v })} />
            </div>
            <div className="mt-6"><Localized label="Şirket Adı" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testId="group-name" /></div>
            <div className="mt-6"><Localized label="Sektör" value={form.sector} onChange={(v) => setForm({ ...form, sector: v })} /></div>
            <div className="mt-6"><Localized label="Slogan" value={form.tagline} onChange={(v) => setForm({ ...form, tagline: v })} /></div>
            <div className="mt-6"><Localized label="Kısa Açıklama" value={form.description} onChange={(v) => setForm({ ...form, description: v })} textarea /></div>
            <div className="mt-6"><Localized label="Uzun Açıklama" value={form.long_description} onChange={(v) => setForm({ ...form, long_description: v })} textarea /></div>

            <div className="mt-8">
              <div className="text-[0.68rem] tracking-[0.24em] uppercase text-brand-medium mb-3">Hizmetler</div>
              <div className="space-y-3">
                {(form.services || []).map((sv, i) => (
                  <div key={i} className="flex gap-2 items-start">
                    <div className="flex-1"><Localized value={sv} onChange={(v) => setForm({ ...form, services: form.services.map((x, j) => (j === i ? v : x)) })} /></div>
                    <Btn variant="danger" onClick={() => setForm({ ...form, services: form.services.filter((_, j) => j !== i) })}><Trash2 size={12} /></Btn>
                  </div>
                ))}
                <Btn variant="ghost" onClick={() => setForm({ ...form, services: [...(form.services || []), { tr: "", en: "", ar: "" }] })} testId="add-service"><Plus size={12} /> Hizmet Ekle</Btn>
              </div>
            </div>
          </Card>

          <Card title="İletişim & Konum">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input label="Telefon" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testId="company-phone" />
              <Input label="E-posta" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testId="company-email" />
              <Input label="Enlem (lat)" value={form.map_lat} onChange={(v) => setForm({ ...form, map_lat: v })} testId="company-lat" />
              <Input label="Boylam (lng)" value={form.map_lng} onChange={(v) => setForm({ ...form, map_lng: v })} testId="company-lng" />
            </div>
            <div className="mt-6"><Localized label="Adres" value={form.address} onChange={(v) => setForm({ ...form, address: v })} textarea /></div>
            <div className="mt-6"><Localized label="Çalışma Saatleri" value={form.working_hours} onChange={(v) => setForm({ ...form, working_hours: v })} /></div>
          </Card>
        </div>
        <div className="space-y-6">
          <Card title="Görseller">
            <ImageUpload label="Kapak Görseli" value={form.cover_image} onChange={(v) => setForm({ ...form, cover_image: v })} testId="group-cover-upload" />
            <div className="mt-4"><ImageUpload label="Logo (opsiyonel)" value={form.logo} onChange={(v) => setForm({ ...form, logo: v })} /></div>
            <div className="mt-4">
              <div className="text-[0.68rem] tracking-[0.24em] uppercase text-brand-medium mb-2">Galeri</div>
              <div className="space-y-2">
                {(form.gallery || []).map((g, i) => (
                  <div key={i} className="flex gap-2 items-start">
                    <div className="flex-1"><ImageUpload label={`Görsel ${i + 1}`} value={g} onChange={(v) => setForm({ ...form, gallery: form.gallery.map((x, j) => (j === i ? v : x)) })} /></div>
                    <Btn variant="danger" onClick={() => setForm({ ...form, gallery: form.gallery.filter((_, j) => j !== i) })}><Trash2 size={12} /></Btn>
                  </div>
                ))}
                <Btn variant="ghost" onClick={() => setForm({ ...form, gallery: [...(form.gallery || []), ""] })} testId="add-gallery"><Plus size={12} /> Görsel Ekle</Btn>
              </div>
            </div>
          </Card>
          <Card title="Yayın">
            <label className="flex items-center gap-3 cursor-pointer">
              <input type="checkbox" checked={!!form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} />
              <span className="text-sm">Ana sayfada öne çıkarılsın</span>
            </label>
          </Card>
        </div>
      </form>
    </div>
  );

  return (
    <div data-testid="admin-group-page">
      <AdminHeader eyebrow="Holding" title="Grup Şirketleri" action={<Btn onClick={() => { setEditing({ new: true }); setForm(empty()); }} testId="new-group"><Plus size={14} /> Yeni</Btn>} />
      <DataTable
        columns={[
          { key: "name", label: "Ad", render: (r) => r.name?.tr || r.slug },
          { key: "sector", label: "Sektör", render: (r) => r.sector?.tr || "" },
          { key: "website_url", label: "Web Sitesi", render: (r) => r.website_url ? <span className="text-xs text-brand-medium truncate max-w-xs inline-block">{r.website_url}</span> : "-" },
          { key: "featured", label: "Öne Çıkan", render: (r) => r.featured ? "✓" : "" },
          { key: "actions", label: "", render: (r) => (
            <div className="flex justify-end gap-2">
              <Btn variant="ghost" onClick={() => { setEditing(r); setForm({ ...empty(), ...r }); }}>Düzenle</Btn>
              <Btn variant="danger" onClick={() => remove(r)}><Trash2 size={12} /></Btn>
            </div>
          ) },
        ]}
        rows={rows}
      />
    </div>
  );
};

export default AdminGroupCompaniesPage;
